/**
 * Centralized API route configuration for B2B app
 * 
 * Maps to actual backoffice Next.js API routes to prevent 404s.
 * All routes are filesystem-based in the backoffice app.
 * 
 * Available backoffice routes (as of Feb 2026):
 * - app/nextapi/structures/[id]/suggestions/status/route.ts (GET/POST)
 * - app/nextapi/structures/[id]/verification-requests/route.ts (GET/POST)
 */

/**
 * Next.js API routes for structures and suggestions
 * 
 * NOTE: The /nextapi/service/structures/{id}/suggestions/summary endpoint may
 * not exist in all backoffice deployments. Use the routes below by default.
 */
export const nextapi = {
  /**
   * Get or update suggestions status for a structure
   * Backoffice route: app/nextapi/structures/[id]/suggestions/status/route.ts
   * 
   * GET: Returns suggestions status summary
   * POST: Updates suggestion item statuses
   */
  suggestionsStatus: (structureId: string) =>
    `/nextapi/structures/${encodeURIComponent(structureId)}/suggestions/status`,

  /**
   * Server-merged suggestions summary (base + overlay statuses)
   * NOTE: This endpoint may not exist in all backoffice deployments.
   */
  suggestionsMergedSummary: (structureId: string) =>
    `/nextapi/service/structures/${encodeURIComponent(structureId)}/suggestions/summary`,

  /**
   * Get verification requests for a structure
   * Backoffice route: app/nextapi/structures/[id]/verification-requests/route.ts
   */
  verificationRequests: (structureId: string) =>
    `/nextapi/structures/${encodeURIComponent(structureId)}/verification-requests`,

  /**
   * Legacy structure details endpoint
   * NOTE: This endpoint may not exist in current backoffice setup.
   * Consider using Firestore data or other available endpoints instead.
   * 
   * @deprecated Use Firestore or specific endpoints instead
   */
  structureDetails: (structureId: string) =>
    `/nextapi/structures/${encodeURIComponent(structureId)}`,
} as const;

/**
 * URL query parameter helpers
 */
export const buildQueryString = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      query.set(key, value ? "1" : "0");
    } else {
      query.set(key, String(value));
    }
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
};
